import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import BlogDetails from "../BlogDetails";
import Tag from "../Tag";
import DOMPurify from "dompurify";
import { Link } from "react-router-dom";

import ShareButtons from "../ShareButtons";
import BlogCard from "../BlogCard";

// Utility function to add IDs to headings dynamically
function addIdsToHeadings(htmlContent) {
  const tempElement = document.createElement("div");
  tempElement.innerHTML = htmlContent;

  const headings = tempElement.querySelectorAll("h2, h3, h4, h5, h6");
  headings.forEach((heading) => {
    if (!heading.id) {
      // Generate an ID based on heading text
      const id = heading.textContent
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^\w-]/g, "")
        .substring(0, 30); // Limit ID length
      heading.id =
        id || `heading-${Math.random().toString(36).substring(2, 9)}`;
    }
  });

  return tempElement.innerHTML;
}

const BlogPage = () => {
  const [blogs, setBlogs] = useState([]);
  const navigate = useNavigate();
  useEffect(() => {
    // Fetch the blogs.json file
    fetch("/blogs/blog.json")
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => setBlogs(data))
      .catch((error) => console.error("Error fetching blogs:", error));
  }, []);

  const { slug } = useParams();
  const blog = blogs.find((blog) => blog.slug === slug);

  const [toc, setToc] = useState([]);
  const [processedContent, setProcessedContent] = useState("");
  const contentRef = useRef(null);

  useEffect(() => {
    if (blog) {
      const updatedContent = addIdsToHeadings(blog.content);
      setProcessedContent(updatedContent);

      const tempElement = document.createElement("div");
      tempElement.innerHTML = updatedContent;
      const headings = Array.from(
        tempElement.querySelectorAll("h2, h3, h4, h5, h6"),
      );

      let h2Counter = 0; // Counter for numbering h2 headings

      const tocData = headings.map((heading) => {
        const level = parseInt(heading.tagName.charAt(1)); // Extract heading level

        // Increment the h2 counter for level 2 headings
        let text = heading.textContent;
        if (level === 2) {
          h2Counter++;
          text = `${h2Counter}. ${text}`; // Prefix the numbering to the text
        }
        if (level === 3) {
          text = `▶ ${text}`; // Prefix the numbering to the text
        }
        if (level === 4) {
          text = `• ${text}`; // Prefix the numbering to the text
        }
        return {
          id: heading.id,
          text,
          level,
        };
      });

      setToc(tocData);
    }
  }, [blog]);

  useEffect(() => {
    const handleHashChange = () => {
      const id = window.location.hash.slice(1);
      const target = document.getElementById(id);
      if (target) {
        target.scrollIntoView({ behavior: "smooth" });
      }
    };

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  useEffect(() => {
    if (!blog && blogs.length > 0) {
      // Redirect to 404 page if blog not found
      navigate("/not-found", { replace: true });
    }
  }, [blog, blogs, navigate]);

  if (!blog) {
    return null; // Prevent rendering while redirecting
  }

  const currentBlogTags = blog.tags;

  const sortedRelatedBlogs = blogs
    .filter((relatedBlog) => {
      return (
        relatedBlog.slug !== blog.slug &&
        relatedBlog.tags.some((tag) => currentBlogTags.includes(tag))
      );
    })
    .sort((a, b) => {
      const aMatches = a.tags.filter((tag) =>
        currentBlogTags.includes(tag),
      ).length;
      const bMatches = b.tags.filter((tag) =>
        currentBlogTags.includes(tag),
      ).length;
      return bMatches - aMatches;
    })
    .slice(0, 6);

  // Capitalized category without hyphens
  const categoryWithoutHyphens = blog.category.replace(/-/g, " ");

  // Page title
  const pageTitle = `${blog.title} - Bafar Labs ${categoryWithoutHyphens} Blog`;

  return (
    <article className="py-20">
      {/* SEO Meta Tags */}

      <title>{pageTitle}</title>
      <meta name="description" content={blog.metaDescription} />
      <meta name="keywords" content={blog.keywords.join(", ")} />

      <link
        rel="canonical"
        href={`https://www.bafarlabs.com/blogs/${blog.slug}`}
      />

      {/* Open Graph (OG) Meta Tags */}
      <meta property="og:type" content="article" />
      <meta property="og:site_name" content="Bafar Labs" />
      <meta property="og:title" content={blog.socialTitle} />
      <meta property="og:description" content={blog.socialDescription} />
      <meta
        property="og:image"
        content={blog.socialImage || "https://www.bafarlabs.com/logo.png"}
      />
      <meta
        property="og:url"
        content={`https://www.bafarlabs.com/blogs/${blog.slug}`}
      />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:creator" content="@BafarLabs" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={blog.socialTitle} />
      <meta name="twitter:description" content={blog.socialDescription} />
      <meta
        name="twitter:image"
        content={blog.socialImage || "https://www.bafarlabs.com/logo.png"}
      />

      <script type="application/ld+json">
        {`
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "${blog.title}",
      "description": "${blog.metaDescription}",
      "author": {
        "@type": "Person",
        "name": "${blog.author}"
      },
      "datePublished": "${blog.datePublished}",
      "dateModified": "${blog.dateModified}",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": "https://www.bafarlabs.com/blogs/${blog.slug}"
      },
      "image": "${blog.featuredImage}",
      "publisher": {
        "@type": "Organization",
        "name": "Bafar Labs",
        "logo": {
          "@type": "ImageObject",
          "url": "https://www.bafarlabs.com/logo.png
          // "
        }
      }
    }
  `}
      </script>

      <div className="relative mb-8 h-[70vh] w-full text-center">
        <div className="absolute left-1/2 top-1/2 z-10 flex w-full -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center">
          <Tag
            name={blog.category}
            link={`/blog-categories/${blog.category
              .toLowerCase()
              .replace(/\s+/g, "-")}`}
            className="px-6 py-2 text-sm"
          />
          <h1 className="relative mt-6 inline-block w-5/6 text-3xl font-semibold capitalize !leading-normal text-[--text-100] sm:text-4xl lg:text-5xl xl:text-7xl">
            {blog.title}
          </h1>
        </div>
        <div className="bg-dark/60 absolute bottom-0 left-0 right-0 top-0 h-full dark:bg-black/65" />
        <img
          src={blog.featuredImage}
          alt={blog.title}
          className="mx-auto h-full w-full object-cover object-center opacity-100"
          loading="lazy"
        />
      </div>

      <BlogDetails blog={blog} slug={slug} />

      <div className="sxl:gap-16 mx-auto mt-8 grid max-w-[77.5rem] grid-cols-12 gap-y-8 px-5 md:px-10 lg:gap-8 lg:px-15 xl:max-w-[110rem]">
        <div className="sm:max-lg::mx-auto col-span-12 sm:max-lg:min-w-[500px] lg:col-span-4">
          {toc.length > 0 && (
            <details
              open
              className="sticky top-28 max-h-[90vh] overflow-hidden overflow-y-auto rounded-lg border border-gray-300 bg-white p-6 pb-24 shadow-lg dark:border-gray-600 dark:bg-neutral-950"
            >
              <summary className="cursor-pointer text-start text-3xl font-semibold capitalize text-slate-300 lg:text-lg dark:text-gray-100">
                Table of Contents
              </summary>
              <ul className="mt-4 space-y-4 text-slate-300 dark:text-gray-300">
                {toc.map((item) => {
                  // Determine padding for each heading level
                  let paddingClass = "";

                  if (item.level === 2) {
                    paddingClass = "pl-0 text-xl"; // No padding for h2
                  } else if (item.level === 3) {
                    paddingClass = "pl-5 text-lg"; // Padding for h3
                  } else if (item.level === 4) {
                    paddingClass = "pl-10 text-base"; // Padding for h4
                  } else if (item.level === 5) {
                    paddingClass = "pl-15 text-sm"; // Padding for h5
                  }

                  return (
                    <li key={item.id} className={`font-medium ${paddingClass}`}>
                      <a
                        href={`#${item.id}`}
                        className={`hover:text-accent dark:hover:text-accentDark transition-colors duration-200 ${
                          item.level === 2 ? "pt-2 font-semibold" : ""
                        }`}
                      >
                        {item.text}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </details>
          )}
        </div>

        <div className="col-span-12 lg:col-span-8">
          <div
            ref={contentRef}
            className="prose prose-invert max-w-none text-base lg:text-lg lg:prose-h2:text-3xl lg:prose-h3:text-2xl lg:prose-h4:text-xl lg:prose-h5:text-lg lg:prose-p:text-lg lg:prose-ul:text-base xl:text-xl xl:prose-h2:text-4xl xl:prose-h3:text-3xl xl:prose-h4:text-2xl xl:prose-h5:text-xl xl:prose-p:text-lg xl:prose-ul:text-base"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(processedContent),
            }}
          />
          <ShareButtons url={blog.canonicalUrl} title={blog.title} />
        </div>
      </div>
      <section className="mb-12 mt-16 flex w-full flex-col items-center justify-center px-5 pb-8 sm:px-10 md:mt-16 md:px-24 xl:px-32">
        <h3 className="text-dark dark:text-light mb-10 inline-block w-fit text-center text-3xl font-bold capitalize md:text-4xl">
          Related Blogs
        </h3>
        <div className="grid grid-cols-1 gap-16 sm:grid-cols-2 min-[1150px]:grid-cols-3">
          {sortedRelatedBlogs.slice(0, 6).map((blog, index) => (
            <BlogCard key={index} blog={blog} /> // Using the reusable component
          ))}
        </div>
        <Link
          to="/blog-categories/all" // Link to view all blogs
          className="text-accent dark:text-accentDark inline-block text-end text-base font-medium underline underline-offset-2 md:text-lg"
        >
          <h3 className="text-dark dark:text-light mt-12 inline-block w-fit text-center text-xl font-bold capitalize md:text-2xl">
            View All
          </h3>
        </Link>
      </section>
    </article>
  );
};

export default BlogPage;
